import { ArrayUtils, HttpStatus } from '@shopping/common'
import { axios } from 'common/utils'
import {
  Brands,
  Category,
  StoreBenefits,
  StoreChannel,
  StoreProducts,
  StoreView,
  StoreZzim,
} from 'common/types/entities/store'
import { EditorContent } from 'common/types/entities/news'
import { ChannelContact, ExternalStatus, SSChannel } from 'common/types/entities/api/channel'
import { Channel } from 'common/types/entities/mongo/channel'
import { excludesSubVertical, isUsingCompositeMenuBySubVertical, Sort, SubVertical } from 'common/constants'
import hostConfig from 'common/config'
import { ZzimResponse } from 'common/types/entities/api/zzim'
import { Error } from 'common/types/entities/error'
import { isErrorRejected } from 'common/utils/ajax/apiErrorHandler'
import { StoreState } from 'common/types/state'
import { ChannelParams, ProductParam } from 'common/types/params'
import { RelationProduct } from 'common/types/entities/mongo/relationProduct'
import { BenefitParam } from 'common/ducks/store/general/benefit'
import BrandInitialIndexType from 'common/constants/brand/initialIndexType'
import { BrandIndexType } from 'common/constants/brand'
import { ROOT_ID } from 'common/types/entities/mongo/menu'
import { fetchMenusOfChannel } from 'common/services/finder/menu'
import { DisplayType } from 'common/ducks/menu'
import { getHeaders } from 'common/utils/ajax/axios'
import { PageType } from 'common/types'

export const fetchSSChannel = async (id: string): Promise<StoreChannel> => {
  const result = await axios.get<StoreChannel>(`/v1/channels/${id}`)
  return result && result.data
}

export const fetchStoreView = async (id: string): Promise<StoreView> => {
  const result = await axios.get<StoreView>(`/v1/channels/${id}/view`)
  return result && result.data
}

export const logStoreNewsPageview = async (channelNo: string, newsId: string) => {
  await axios.post(
    `/v1/channels/${channelNo}/news/${newsId}/log`,
    {},
    {
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  )
}

export const increaseStoreView = async (id: string) => {
  await axios.post(
    `/v1/channels/${id}/view`,
    {},
    {
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  )
}

export async function fetchStoreBenefits(
  storeAccountNo: number,
  params: BenefitParam,
  headers?: any,
): Promise<StoreBenefits[]> {
  const result = await axios
    .get<StoreBenefits[]>(`/v1/accounts/${storeAccountNo}/coupons`, {
      params,
      ...getHeaders(headers),
      data: { [isErrorRejected]: true },
    })
    .catch(error => {
      // throw error (error 화면이 나올 수 있어 제거, benefit만 안나오는게 좋음)
      return null
    })

  return result && result.data
}

export async function fetchStoreGrades(
  storeAccountNo: number,
  storeId: string,
  headers?: any,
): Promise<StoreBenefits[]> {
  const result = await axios
    .get<StoreBenefits[]>(`/v1/accounts/${storeAccountNo}/grades`, {
      params: {
        storeId,
      },
      ...getHeaders(headers),
      data: { [isErrorRejected]: true },
    })
    .catch(error => {
      return null
    })
  return result && result.data
}

export const fetchStoreZzim = async (storeId: string, headers?: any): Promise<StoreZzim> => {
  const result = await axios.get<StoreZzim>(`/v1/channels/${storeId}/zzim`, getHeaders(headers))
  return result && result.data
}

export async function setStoreZzim(storeId: string, hasUserStoreZzim: boolean): Promise<ZzimResponse> {
  const { zzimUri } = hostConfig

  const apiUrl = hasUserStoreZzim
    ? zzimUri[process.env.PROJECT_NAME].removeStoreZzimUri
    : zzimUri[process.env.PROJECT_NAME].setStoreZzimUri

  const response = await axios.get<ZzimResponse>(apiUrl, {
    params: {
      channelNo: storeId,
    },
    withCredentials: true,
  })

  if (!response) {
    throw new Error(`setStoreZzim ${storeId}`)
  }

  const responseData = response.data

  if (responseData.sErrorCode) {
    const errorResponse = response.data
    const error: Error = {
      status: HttpStatus.UNAUTHORIZED,
      message: 'Unauthorized Naver',
      detailMessage: errorResponse.sErrorAlertMessage,
    }
    throw error
  }

  if (!responseData.bSuccess && responseData.htReturnValue && responseData.htReturnValue.sErrorCode) {
    const errorResponse = responseData.htReturnValue

    const httpError: Error = {
      status: HttpStatus.OK,
      message: errorResponse.sMessage,
    }
    throw httpError
  }

  return response && response.data
}

export async function fetchStoreProducts(
  storeId: string,
  page: number,
  pageSize: number,
  sort: Sort,
  categoryId?: number,
  brandId?: number,
  includeItemCount?: boolean,
  filterSoldOut?: boolean,
): Promise<StoreProducts> {
  const params = {
    page,
    pageSize,
  }
  sort && (params['sort'] = sort)
  categoryId && (params['categoryId'] = categoryId)
  brandId && (params['brandId'] = brandId)
  includeItemCount && (params['includeItemCount'] = includeItemCount)
  filterSoldOut && (params['filterSoldOut'] = filterSoldOut)

  const result = await axios.get<StoreProducts>(`/v1/channels/${storeId}/products`, {
    params,
  })

  return result && result.data
}

export async function fetchStoreProductsByIds(params: ProductParam): Promise<StoreProducts> {
  const { storeId, page, storeCategoryId } = params
  let url = ''
  if (storeId) {
    url = `/v1/channels/${storeId}/products`
  } else if (storeCategoryId) {
    url = `/v1/branch/${storeCategoryId}/products`
  }

  const result = await axios.get<StoreProducts>(url, {
    params,
  })

  if (result && result.data) {
    result.data.products.forEach(product => {
      product['groupKey'] = page
    })
  }

  return (
    result && {
      storeId,
      ...result.data,
    }
  )
}

export async function fetchStoreCategories(storeId: string, brandId?: string): Promise<Category[]> {
  const params = { brandId }
  const result = await axios.get<Category[]>(`/v1/channels/${storeId}/categories`, {
    params,
  })
  return result && result.data
}

export async function fetchStoreBrandNews(storeId: string): Promise<EditorContent> {
  const result = await axios.get<EditorContent>(`/v1/channels/${storeId}/brand-news`)
  return result && { ...result.data, storeId }
}

export async function fetchStoreBrands(storeId: string): Promise<Brands> {
  const result = await axios.get<Brands>(`/v1/channels/${storeId}/brands`)
  return result && { ...result.data, storeId }
}

export const isValidChannel = (channel: SSChannel): boolean => {
  if (!channel) return false
  if (['development', 'test'].includes(process.env.NODE_ENV)) return true
  const validStatus = [ExternalStatus.NORMAL, ExternalStatus.LEAVE_APPLY]
  return validStatus.includes(channel.channelExternalStatusType) && channel.exhibitionYn
}

export const isValidRelationProduct = (relationProduct: RelationProduct): boolean => {
  if (!relationProduct) return false
  if (['development', 'test'].includes(process.env.NODE_ENV)) return true
  return relationProduct.channelDisplayable
}

export const fetchChannels = async (params: ChannelParams): Promise<StoreState> => {
  const result = await axios.get<StoreState>(`/v1/channels`, {
    params,
  })
  return result && result.data
}

export const fetchChannelsByIndex = async (
  subVertical: SubVertical,
  initialIndexType: BrandInitialIndexType,
): Promise<Channel[]> => {
  const result = await axios.get(`/v1/channels/subvertical/${subVertical}/initial`, {
    params: { initialIndexType },
  })
  return result && result.data
}

export const fetchChannelsIndexList = async (subVertical: SubVertical, indexType: BrandIndexType) => {
  if (!indexType) return
  const result = await axios.get(`/v1/channels/subvertical/${subVertical}/indexes/${indexType}`)
  return result && result.data
}

export const getStoreFormattedTelNo = (contactInfo: ChannelContact): string => {
  if (!contactInfo || !contactInfo.displayTelNo) return null

  return contactInfo.disabledDomesticTelNo || !contactInfo.telNo || !contactInfo.telNo.formattedNumber
    ? contactInfo.overseaTelNo && contactInfo.overseaTelNo.formattedNumber
    : contactInfo.telNo && contactInfo.telNo.formattedNumber
}

export const popupReportWrongNumber = (channelId: string) => {
  window.open(
    `/report/wrongNumber/${channelId}`,
    'reportWrongNumber',
    'width=800px,height=754px,location=no,resizable=0',
  )
}

export const reportStoreWrongNumber = async (channelId, formatNumber) => {
  try {
    return await axios.post(`/v1/channels/${channelId}/report/wrongNumber`, { formatNumber, [isErrorRejected]: true })
  } catch (e) {
    return e.response
  }
}
export const getStoreHomeInitMenuId = async (channelId: string, subVertical: SubVertical): Promise<string> => {
  if (isUsing1stMenuLayerOnStoreHome(subVertical) && !isUsingCompositeMenuBySubVertical(subVertical)) return ROOT_ID

  const firstMenus = await fetchMenusOfChannel(channelId, ROOT_ID, DisplayType.NO_RULE)

  if (ArrayUtils.isEmpty(firstMenus)) return ROOT_ID

  if (isStyleWithMoreThan2Menus(subVertical, firstMenus)) return ROOT_ID

  if (subVertical === SubVertical.HOME_MEAL) return firstMenus.find(menu => menu.alias === 'food_cooking').id

  if (subVertical === SubVertical.FOREIGN) return firstMenus.find(menu => menu.alias === 'PRODUCT').id

  return ArrayUtils.getFirst(firstMenus, {}).id
}

const isUsing1stMenuLayerOnStoreHome = excludesSubVertical(
  SubVertical.STYLE,
  SubVertical.HOME_LIVING,
  SubVertical.HANDMADE,
  SubVertical.STATIONERY,
  SubVertical.HOME_MEAL,
)

const isStyleWithMoreThan2Menus = (subVertical: SubVertical, firstMenus) =>
  subVertical === SubVertical.STYLE && ArrayUtils.size(firstMenus) >= 2

export const isChannelWithHighRatingReview = async (id: string): Promise<boolean> => {
  const result = await axios.get<boolean>(`/v1/channels/${id}/has-high-rating-review`)
  return result && result.data
}

export async function fetchChannelsWithHighRatingReviews(subVertical: SubVertical, menu: string): Promise<Channel[]> {
  const result = await axios.get<Channel[]>('/v1/channels/channels-with-high-rating-reviews', {
    params: { menu, subVertical },
  })
  return result && result.data
}

export const convertPrivacyChannel = (channel: SSChannel, pageType?: PageType): SSChannel => {
  if (!channel) return channel

  const { contactInfo } = channel
  if (pageType && pageType === PageType.STORE) {
    channel.chrgrEmail = undefined
    if (contactInfo) {
      if (contactInfo.telNo && contactInfo.telNo.phoneNo) {
        contactInfo.telNo.phoneNo = undefined
      }
      if (contactInfo.overseaTelNo && contactInfo.overseaTelNo.phoneNo) {
        contactInfo.overseaTelNo.phoneNo = undefined
      }
    }
  }

  return channel
}
